<template>
  <v-form ref="form">
    <FormPanels>
      <FormPanel label="Details">
        <FormRow
          required
          label="Title"
          tooltip="This is the title for your Schema."
        >
          <TextField
            :rules="[validation.required]"
            placeholder="Title"
            v-model="form.title"
          ></TextField>
        </FormRow>

        <FormRow required label="Singular Version of Title">
          <template #tooltip>
            If set, will use this for the "Add {title}" and "Update {title}"
            buttons instead of the plural schema title.
          </template>
          <TextField placeholder="" v-model="form.title_singular"></TextField>
        </FormRow>

        <FormRow
          required
          label="Tech Name"
          tooltip="This is the Tech Name for your Schema."
        >
          <TextField
            :rules="[validation.required]"
            placeholder="Tech Name"
            v-model="form.tech_name"
          ></TextField>
        </FormRow>
        <FormRow
          required
          label="Internal"
          tooltip="Enable this option to hide the form from non-Admins"
          v-if="['super_admin'].includes(currentUser.active_team_role)"
        >
          <Checkbox placeholder="Internal" v-model="form.internal"></Checkbox>
        </FormRow>
        <FormRow required label="Schema Type">
          <Dropdown
            placeholder="Schema Type"
            v-model="form.schema_type"
            :items="schemaTypes"
            :disabled="formScope !== 'new'"
          ></Dropdown>
        </FormRow>
        <FormRow
          required
          label="Enable Lenis by Default"
          v-if="form.schema_type == 'page'"
        >
          <Checkbox
            placeholder="Enable Lenis by Default"
            v-model="form.enable_lenis_by_default"
          ></Checkbox>
        </FormRow>
        <FormRow
          label="Stickied"
          tooltip="Enable this option to pin schema at the top of your list"
        >
          <Checkbox placeholder="Stickied" v-model="form.is_sticky"></Checkbox>
        </FormRow>
        <FormRow
          label="Environment Agnostic"
          v-if="['super_admin'].includes(currentUser.active_team_role)"
        >
          <template #tooltip>
            Entities in this schema will be shared across staging and
            production.
            <template v-if="form.id">
              <br /><br />
              <strong>Warning:</strong> Toggling this on an existing schema will
              make any entities stored in the production table unreachable in
              the admin panel. Ensure staging and production data are in sync
              before enabling.
            </template>
          </template>
          <Checkbox
            placeholder="Environment Agnostic"
            :value="form.environment_agnostic"
            @input="onEnvironmentAgnosticChange"
          ></Checkbox>
        </FormRow>
        <FormRow
          label="Default Virtual Component"
          v-if="form.schema_type === 'page'"
          tooltip="If set, this custom component will be added to all entities/pages created under this schema"
        >
          <CustomComponentSelector
            v-model="form.default_virtual_component_id"
          />
        </FormRow>

        <FormRow label="Dynamic Page" v-if="form.schema_type === 'regular'">
          <template #tooltip
            >Optional. If entities in this schema will be shown on a dynamic
            page (with a url like “/work/:work_name”, where “:work_name” is
            replaced by the entity’s tech name), select that page
            here.</template
          >
          <PagesSelector
            multiple
            v-model="form.page_ids"
            :condition="(page) => page.url && page.url.indexOf(':') != -1"
          ></PagesSelector>
        </FormRow>

        <template v-if="['regular', 'page'].includes(form.schema_type)">
          <FormRow label="Use Random Tech Names">
            <template #tooltip>
              Hides the "Tech name" field and generates a random one.
            </template>
            <Checkbox v-model="form.use_random_tech_names"></Checkbox>
          </FormRow>
        </template>

        <template v-if="form.schema_type === 'page'">
          <FormRow label="Page URL Prefix" :tooltip-max-width="400">
            <template #tooltip>
              <p>
                If specified, will lock pages created under this schema to a
                custom URL prefix.
              </p>
              <p>
                For example, <code>/posts/</code> would only allow page URLs
                like <code>/posts/example-post</code> and would disallow
                <code>/posts/another-post/foo</code> or
                <code>/example-post</code>.
              </p>
            </template>
            <TextField
              placeholder="/posts/"
              v-model="form.page_prefix"
              :rules="[rules.pagePrefix]"
            >
            </TextField>
            <div v-if="pathPrefixHasUnderscore" class="warning-message mt-1">
              ⚠️ Underscores in URLs are less readable than hyphens.
            </div>
          </FormRow>
        </template>
      </FormPanel>

      <FormPanel label="Schema">
        <div
          class="schema-field"
          v-for="(field, idx) in form.schema"
          :key="idx"
        >
          <div class="schema-field-menu">
            <v-select
              label="Type"
              :items="fieldTypes"
              v-model="field.type"
              :rules="[validation.required]"
              hide-details
            ></v-select>
            <v-text-field
              label="Field Name"
              v-model="field.title"
              :rules="[validation.required]"
              hide-details
            ></v-text-field>
            <v-text-field
              label="Tech Name"
              v-model="field.tech_name"
              hide-details
              :rules="[validation.required]"
            ></v-text-field>
            <v-text-field
              label="Helper Text"
              v-model="field.helper_text"
              hide-details
            ></v-text-field>
          </div>
          <div class="schema-field-component">
            <v-checkbox
              v-model="field.required"
              label="Required"
              v-if="isRequirable(field.type)"
            ></v-checkbox>
            <component
              :value="field"
              :is="`SchemaField${field.type}`"
              :schema-uuid="form.id"
              @update="update(idx, $event)"
            ></component>
          </div>
          <div class="schema-field__btns">
            <v-btn icon @click="up(idx)">
              <v-icon>$arrow_up</v-icon>
            </v-btn>
            <v-btn icon @click="down(idx)">
              <v-icon>$arrow_down</v-icon>
            </v-btn>
            <v-btn @click="deleteField(idx)" icon>
              <v-icon> $delete </v-icon>
            </v-btn>
          </div>
        </div>
        <v-btn @click="addField" color="primary" block large outlined
          >+ Add Field / Relation</v-btn
        >
      </FormPanel>
    </FormPanels>
    <SubmitFooter :refs="$refs" :submit="submit">{{ btnText }}</SubmitFooter>
  </v-form>
</template>

<script>
import mixin from "../_extends/form.extends.js";
export default {
  extends: mixin,
  data() {
    return {
      fieldTypes: [
        "TextField",
        "TextArea",
        "RichText",
        "Html",
        "Number",
        "DateTime",
        "Boolean",
        "Image",
        "Select",
        "MultiSelect",
        "MultiText",
        "HasMany",
        "HasOne",
        "VimeoVideo",
        "PageSelect",
      ],
      schemaTypes: [
        { text: "Default", value: "regular" },
        { text: "Singleton", value: "singleton" },
        { text: "Page", value: "page" },
        { text: "Dynamic Page", value: "dynamic_page" },
      ],
      rules: {
        pagePrefix: (v) => {
          if (!v) return true; // Allow empty

          if (!v.startsWith("/")) return "Prefix must start with /";
          if (!v.endsWith("/")) return "Prefix must end with /";
          if (!/^\/[\w\-/]*\/$/.test(v))
            return "Prefix can only contain letters, numbers, hyphens, and slashes";
          if (/\/\//.test(v))
            return "Prefix cannot contain consecutive slashes";

          return true;
        },
      },
    };
  },
  methods: {
    async onEnvironmentAgnosticChange(val) {
      if (val && this.form.id) {
        const confirmed = await this.$confirmWindow({
          title: "Enable Environment Agnostic feature?",
          description:
            "Any entities stored in the production table for this schema will become unreachable in the admin panel. Make sure staging and production data are in sync before proceeding.",
        });
        if (!confirmed) {
          // The checkbox has already toggled internally to true. Force a prop
          // round-trip so it reverts: set true (matches checkbox state) → flush
          // render → set false (drives checkbox back to unchecked).
          this.form.environment_agnostic = true;
          await this.$nextTick();
          this.form.environment_agnostic = false;
          return;
        }
      }
      this.form.environment_agnostic = val;
    },
    up(idx) {
      if (idx < 1) return;
      const { schema } = this.form;
      const element = { ...schema[idx] };
      schema.splice(idx, 1);
      schema.splice(idx - 1, 0, element);
    },
    down(idx) {
      const { schema } = this.form;
      if (idx >= schema.length - 1) return;
      const element = { ...schema[idx] };
      schema.splice(idx, 1);
      schema.splice(idx + 1, 0, element);
    },
    addField() {
      this.form.schema.push({
        title: "",
        type: "TextField",
        tech_name: "",
      });
    },
    async deleteField(idx) {
      const confirmed = await this.$confirmWindow({ title: "Are you sure?" });
      if (!confirmed) return;
      this.form.schema.splice(idx, 1);
    },
    update(idx, $event) {
      this.form.schema.splice(idx, 1, $event);
    },
    isRequirable(fieldType) {
      return !["HasMany", "HasOne", "Html", "MultiSelect", "MultiText", "RichText"].includes(
        fieldType
      );
    },
  },
  computed: {
    formScope() {
      // Split the pathname into segments using "/" as the delimiter
      const segments = window.location.pathname.split("/");
      // Get the last segment (the last part of the URL)
      return segments[segments.length - 1];
    },
    pathPrefixHasUnderscore() {
      return this.form.page_prefix && this.form.page_prefix.includes("_");
    },
  },
  created() {
    if (
      this.currentUser.active_project.enabled_addon_names.includes(
        "Addon::Greenhouse"
      )
    ) {
      this.fieldTypes.push("GreenhouseApplicationSelect");
    }
  },
};
</script>

<style scoped lang="scss">
.schema-field {
  padding: 15px;
  border: 2px solid;
  margin-bottom: 10px;
  position: relative;

  & .form-row,
  &::v-deep .form-row {
    max-width: calc(100% - 100px);
  }

  &__btns {
    position: absolute;
    right: 10px;
    top: 10px;
  }

  &-menu {
    display: flex;
    align-items: center;
    max-width: calc(100% - 100px);

    & > * {
      margin: 0 10px;
    }
  }

  &-component {
    padding: 10px;
    max-width: 550px;
  }
}

.warning-message {
  color: #787878;
  font-size: 14px;
}
</style>
